import React, { useState, useEffect, useCallback } from "react";
import { Copy, Check, RefreshCw, Wallet, Layers } from "lucide-react";
import { getWalletBalances } from "../../lib/investApi";

const CATEGORY_META = {
  SUV:      { icon: "🚙", label: "SUV Token",       accent: "#2563EB" },
  SEDAN:    { icon: "🚗", label: "Sedan Token",     accent: "#E11D48" },
  HATCHBACK:{ icon: "🚕", label: "Hatchback Token", accent: "#059669" },
  TRUCK:    { icon: "🛻", label: "Truck Token",     accent: "#ea580c" },
  SPORTS:   { icon: "🏎️", label: "Sports Token",    accent: "#9333ea" },
};

function StatPill({ label, value, sub }) {
  return (
    <div style={{
      flex: 1,
      display: "flex",
      flexDirection: "column",
      gap: 2,
      padding: "12px 14px",
      background: "var(--zc-bg-3)",
      borderRadius: 10,
      border: "1px solid var(--zc-border)",
    }}>
      <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.07em", textTransform: "uppercase", color: "var(--zc-text-3)" }}>{label}</span>
      <span className="zc-stat" style={{ fontSize: 22, lineHeight: 1.1 }}>{value}</span>
      {sub && <span style={{ fontSize: 11, color: "var(--zc-text-3)" }}>{sub}</span>}
    </div>
  );
}

function SkeletonPill() {
  return (
    <div style={{ flex: 1, height: 72, borderRadius: 10, background: "var(--zc-bg-3)", animation: "pulse 1.5s ease-in-out infinite" }} />
  );
}

function SkeletonRow() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 10, background: "var(--zc-bg-3)", animation: "pulse 1.5s ease-in-out infinite" }}>
      <div style={{ width: 40, height: 40, borderRadius: 8, background: "var(--zc-border)" }} />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ height: 12, width: "45%", borderRadius: 4, background: "var(--zc-border)" }} />
        <div style={{ height: 10, width: "25%", borderRadius: 4, background: "var(--zc-border)" }} />
      </div>
      <div style={{ height: 20, width: 40, borderRadius: 4, background: "var(--zc-border)" }} />
    </div>
  );
}

export default function InvestPortfolioTab({ walletUserId, refreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    if (!walletUserId) return;
    setLoading(true);
    setError(null);
    try {
      const bal = await getWalletBalances(walletUserId);
      setData(bal);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [walletUserId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const handleCopy = () => {
    if (data?.walletAddress) {
      navigator.clipboard.writeText(data.walletAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }
  };

  if (!walletUserId) {
    return (
      <div style={{ textAlign: "center", padding: "72px 0", color: "var(--zc-text-3)" }}>
        <Wallet size={36} style={{ margin: "0 auto 14px", display: "block", opacity: 0.35 }} />
        <p style={{ fontSize: 14 }}>Sign in to view your portfolio.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "14px 16px", background: "#FEF2F2", borderRadius: 10, color: "var(--zc-danger)", fontSize: 14, border: "1px solid #FECACA" }}>
        {error}
      </div>
    );
  }

  const tokens = data?.tokens ?? [];
  const totalTokens = tokens.reduce((s, t) => s + Number(t.balance || 0), 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20, maxWidth: 780 }}>

      {/* ── Wallet card ── */}
      <div className="zc-card" style={{ padding: 0, overflow: "hidden" }}>
        {/* Card header strip */}
        <div style={{
          padding: "14px 20px",
          borderBottom: "1px solid var(--zc-border)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "var(--zc-bg-2)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{
              width: 30, height: 30, borderRadius: 8,
              background: "var(--zc-secondary-soft)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <Wallet size={15} style={{ color: "var(--zc-secondary)" }} />
            </div>
            <span style={{ fontWeight: 600, fontSize: 13, color: "var(--zc-text)" }}>Investment Wallet</span>
          </div>

          <button
            onClick={load}
            disabled={loading}
            aria-label="Refresh portfolio"
            style={{
              width: 32, height: 32,
              display: "flex", alignItems: "center", justifyContent: "center",
              border: "1.5px solid var(--zc-border)",
              borderRadius: 8,
              background: "transparent",
              cursor: loading ? "default" : "pointer",
              color: "var(--zc-text-3)",
              transition: "border-color 0.15s, color 0.15s, background 0.15s",
              opacity: loading ? 0.5 : 1,
            }}
            onMouseEnter={e => { if (!loading) { e.currentTarget.style.borderColor = "var(--zc-secondary)"; e.currentTarget.style.color = "var(--zc-secondary)"; e.currentTarget.style.background = "var(--zc-secondary-soft)"; }}}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--zc-border)"; e.currentTarget.style.color = "var(--zc-text-3)"; e.currentTarget.style.background = "transparent"; }}
          >
            <RefreshCw size={13} style={{ animation: loading ? "spin 0.8s linear infinite" : "none" }} />
          </button>
        </div>

        {/* Card body */}
        <div style={{ padding: "18px 20px", display: "flex", flexDirection: "column", gap: 16 }}>

          {/* Wallet address */}
          <div style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "10px 14px",
            background: "var(--zc-bg-3)",
            borderRadius: 10,
            border: "1px solid var(--zc-border)",
          }}>
            <div style={{
              width: 7, height: 7, borderRadius: "50%",
              background: "var(--zc-success)",
              flexShrink: 0,
              boxShadow: "0 0 0 3px rgba(16,185,129,0.15)",
            }} />
            {loading && !data ? (
              <div style={{ flex: 1, height: 12, borderRadius: 4, background: "var(--zc-border)", animation: "pulse 1.5s ease-in-out infinite" }} />
            ) : (
              <span style={{
                flex: 1,
                fontFamily: "var(--zc-font-mono)",
                fontSize: 11,
                color: "var(--zc-text-2)",
                wordBreak: "break-all",
                lineHeight: 1.5,
              }}>
                {data?.walletAddress ?? "—"}
              </span>
            )}
            <button
              onClick={handleCopy}
              disabled={!data?.walletAddress}
              aria-label={copied ? "Address copied" : "Copy wallet address"}
              style={{
                flexShrink: 0,
                display: "flex", alignItems: "center", gap: 4,
                padding: "5px 10px",
                borderRadius: 6,
                border: "1.5px solid",
                borderColor: copied ? "var(--zc-success)" : "var(--zc-border-2)",
                background: copied ? "rgba(16,185,129,0.08)" : "var(--zc-bg-2)",
                color: copied ? "var(--zc-success)" : "var(--zc-text-2)",
                fontSize: 11, fontWeight: 600,
                cursor: data?.walletAddress ? "pointer" : "default",
                transition: "all 0.15s",
              }}
            >
              {copied
                ? <><Check size={11} /> Copied</>
                : <><Copy size={11} /> Copy</>
              }
            </button>
          </div>

          {/* Stats row */}
          <div style={{ display: "flex", gap: 10 }}>
            {loading && !data ? (
              <><SkeletonPill /><SkeletonPill /><SkeletonPill /></>
            ) : (
              <>
                <StatPill
                  label="ETH Balance"
                  value={data ? parseFloat(data.ethBalance).toFixed(4) : "—"}
                  sub="for gas fees"
                />
                <StatPill
                  label="Token Types"
                  value={data?.tokens?.length ?? 0}
                  sub="collections held"
                />
                <StatPill
                  label="Total Tokens"
                  value={totalTokens}
                  sub="tokens owned"
                />
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Holdings card ── */}
      <div className="zc-card" style={{ padding: 0, overflow: "hidden" }}>
        <div style={{
          padding: "14px 20px",
          borderBottom: "1px solid var(--zc-border)",
          background: "var(--zc-bg-2)",
          display: "flex", alignItems: "center", gap: 8,
        }}>
          <div style={{
            width: 30, height: 30, borderRadius: 8,
            background: "var(--zc-secondary-soft)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Layers size={15} style={{ color: "var(--zc-secondary)" }} />
          </div>
          <span style={{ fontWeight: 600, fontSize: 13, color: "var(--zc-text)" }}>Token Holdings</span>
          {data && tokens.length > 0 && (
            <span style={{
              marginLeft: "auto",
              fontSize: 11, fontWeight: 600,
              padding: "2px 8px",
              borderRadius: 20,
              background: "var(--zc-secondary-soft)",
              color: "var(--zc-secondary)",
            }}>
              {tokens.length} type{tokens.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>

        <div style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
          {loading && !data ? (
            <><SkeletonRow /><SkeletonRow /><SkeletonRow /></>
          ) : tokens.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 20px" }}>
              <div style={{
                width: 52, height: 52, borderRadius: 14,
                background: "var(--zc-bg-3)",
                display: "flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto 14px",
                fontSize: 24,
              }}>🪙</div>
              <p style={{ fontWeight: 600, fontSize: 14, color: "var(--zc-text-2)", marginBottom: 4 }}>No tokens yet</p>
              <p style={{ fontSize: 13, color: "var(--zc-text-3)" }}>Buy your first collection to get started.</p>
            </div>
          ) : (
            tokens.map(t => {
              const catMeta = CATEGORY_META[t.category];
              const accent = catMeta?.accent ?? "var(--zc-secondary)";
              const balance = Number(t.balance || 0);
              const share = totalTokens > 0 ? Math.round((balance / totalTokens) * 100) : 0;

              return (
                <div
                  key={t.tokenId}
                  style={{
                    display: "flex", alignItems: "center", gap: 14,
                    padding: "12px 14px",
                    background: "var(--zc-bg-3)",
                    borderRadius: 10,
                    border: "1px solid var(--zc-border)",
                    borderLeft: `3px solid ${accent}`,
                    transition: "border-color 0.15s, background 0.15s",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = "var(--zc-bg-2)"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "var(--zc-bg-3)"; }}
                >
                  {/* Icon / image */}
                  {t.imageUrl ? (
                    <img
                      src={t.imageUrl}
                      alt={catMeta?.label ?? t.name}
                      style={{ width: 40, height: 40, borderRadius: 10, objectFit: "cover", flexShrink: 0 }}
                    />
                  ) : (
                    <div style={{
                      width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                      background: `${accent}18`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 20,
                    }}>
                      {catMeta?.icon ?? "🚘"}
                    </div>
                  )}

                  {/* Name + allocation bar */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {catMeta?.label ?? t.name}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ flex: 1, height: 3, borderRadius: 2, background: "var(--zc-border)", overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${share}%`, background: accent, borderRadius: 2, transition: "width 0.4s" }} />
                      </div>
                      <span style={{ fontSize: 10, color: "var(--zc-text-3)", flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>{share}%</span>
                    </div>
                  </div>

                  {/* Balance */}
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div className="zc-stat" style={{ fontSize: 18, lineHeight: 1 }}>{balance}</div>
                    <div style={{ fontSize: 10, color: "var(--zc-text-3)", marginTop: 2, textTransform: "uppercase", letterSpacing: "0.05em" }}>tokens</div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
